import { motion, useReducedMotion, type Transition } from "framer-motion";
import { ArrowLeftIcon, Loader2Icon } from "lucide-react";
import { NextStepProvider } from "nextstepjs";
import { lazy, Suspense, useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import type { PlaygroundEvaluateSheetReference } from "@/queries/playground/evaluate";
import type { ScorecardViewState } from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/types";
import { SmartTableProvider } from "@/routes/smart-table/smart-table-context";
import { SmartTableTourProvider } from "@/routes/smart-table/tour/context";

const SmartTablePage = lazy(() => import("@/routes/smart-table/components/SmartTablePage"));

interface ImportedSheetEditorProps {
  open: boolean;
  reference: PlaygroundEvaluateSheetReference | null;
  onOpenChange: (open: boolean) => void;
  onChangesSettled: () => void;
  initialScorecardStepId?: string | null;
}

const ImportedSheetEditor = ({
  open,
  reference,
  onOpenChange,
  onChangesSettled,
  initialScorecardStepId,
}: ImportedSheetEditorProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [headerActionsContainer, setHeaderActionsContainer] = useState<HTMLElement | null>(null);
  const shouldReduceMotion = useReducedMotion();

  if (!reference) {
    return null;
  }

  const isDraft = reference.source_type === "playground_draft" && !reference.exported_at;
  const initialScorecardView: ScorecardViewState | undefined = initialScorecardStepId
    ? { view: "criterion-edit", stepId: initialScorecardStepId }
    : undefined;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    if (!isSaving && !isClosing) {
      setIsClosing(true);
    }
  };

  let panelTransition: Transition = {
    type: "spring",
    stiffness: 380,
    damping: 36,
    mass: 0.9,
  };
  if (shouldReduceMotion) {
    panelTransition = { duration: 0 };
  } else if (isClosing) {
    panelTransition = { duration: 0.22, ease: [0.32, 0, 0.67, 0] };
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        shouldShowCloseButton={false}
        overlayClassName="z-30 bg-gray-950/15 duration-200 ease-out motion-reduce:duration-0"
        className="!left-0 !top-0 z-40 !h-[100dvh] !w-screen !max-w-none !translate-x-0 !translate-y-0 !animate-none overflow-hidden !rounded-none border-0 bg-transparent p-0 shadow-none sm:!rounded-none md:!w-screen"
      >
        <DialogTitle className="sr-only">
          Edit {reference.smart_table_title} / {reference.sheet_title}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Edit the imported sheet without leaving the Playground.
        </DialogDescription>
        <motion.div
          initial={shouldReduceMotion ? false : { x: "100%", opacity: 0.98 }}
          animate={{ x: isClosing ? "100%" : 0, opacity: isClosing ? 0.98 : 1 }}
          transition={panelTransition}
          onAnimationComplete={() => {
            if (isClosing) {
              setIsClosing(false);
              onOpenChange(false);
            }
          }}
          className="flex h-full w-full flex-col overflow-hidden bg-background shadow-[-24px_0_64px_rgba(15,23,42,0.16)]"
        >
          <header className="flex h-12 shrink-0 items-center border-b border-border/70 bg-background px-3">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={isSaving}
              onClick={() => handleOpenChange(false)}
              className="h-9 shrink-0 px-2 text-muted-foreground hover:text-foreground"
            >
              {isSaving ? (
                <Loader2Icon className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowLeftIcon className="h-4 w-4" />
              )}
              {isSaving ? "Saving changes..." : "Back to Playground"}
            </Button>
            <div className="ml-2 flex min-w-0 items-center gap-1.5 border-l border-border/70 pl-3">
              <span className="truncate text-sm font-medium text-foreground">
                {reference.sheet_title}
              </span>
              <span className="hidden truncate text-xs text-muted-foreground sm:inline">
                from {reference.smart_table_title}
              </span>
            </div>
            <div
              ref={setHeaderActionsContainer}
              className="ml-auto flex min-w-0 shrink-0 items-center pl-3"
            />
          </header>
          <div className="min-h-0 flex-1">
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                  {isDraft ? "Opening Draft…" : "Opening Sheet…"}
                </div>
              }
            >
              <NextStepProvider>
                <SmartTableProvider>
                  <SmartTableTourProvider>
                    <SmartTablePage
                      tableId={reference.smart_table_id}
                      initialSheetId={reference.sheet_id}
                      syncUrl={false}
                      hideBreadcrumbs
                      singleSheetMode
                      onChangesSettled={onChangesSettled}
                      onSavingChange={setIsSaving}
                      focusedHeaderActionsContainer={headerActionsContainer}
                      initialRightPanel={isDraft || initialScorecardStepId ? "score" : undefined}
                      initialScorecardView={initialScorecardView}
                    />
                  </SmartTableTourProvider>
                </SmartTableProvider>
              </NextStepProvider>
            </Suspense>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportedSheetEditor;
